<template lang="html">
  <section class="m-column" :id="id" v-if="isReady">
    <div class="m-row">
      <div class="m-column_element">
        <div class="m-column_item">
          {{ getMessageText(messages.TABLE_COL_HEADER) }}
        </div>
        <expressions
          class="input-fields"
          :data-elements="allSources"
          :data-type="'string'"
          :initial-expression="header"
          :is-global="true"
          :content-type="appType"
          @on-expression-field-valid="onConfigExpressionValid"
        />
      </div>
    </div>
    <div class="m-row">
      <div class="m-column_element">
        <div class="m-column_item">
          {{ $t("userSortable") }}
        </div>
        <b-form-checkbox
          v-model="userSortable"
          @change="onUserSortableChanged"
        ></b-form-checkbox>
      </div>
    </div>
    <div class="m-row">
      <div class="m-column_element">
        <div class="m-column_item">
          {{ getMessageText(messages.TABLE_COL_VALUE) }}
        </div>
        <search-box-simple
          ref="valueFields"
          class="input-fields"
          :list="valueOptions"
          :value="value"
          :property="propName"
          :has-icon="true"
          @on-search-box-simple-clear-selected="onValueSelected"
          @on-search-box-item-selected="onValueSelected"
        />
      </div>
    </div>
    <div class="m-row" v-if="isDateFormat">
      <div class="m-column_element">
        <div class="m-column_item"></div>
        <div class="checkbox-wrapper stack-checkbox">
          <label
            class="no-margin cursor-pointer"
            @click="toggleIsLocaleAware"
            >{{ $t("useLocaleFormatting") }}</label
          >
          <b-form-checkbox
            v-model="isLocaleAwareFormatting"
            @change="onCheckboxChanged"
          ></b-form-checkbox>
        </div>
      </div>
    </div>
    <div class="m-row" v-if="isDateFormat && !isLocaleAwareFormatting">
      <div class="m-column_element">
        <div class="m-column_item">{{ getMessageText(messages.FORMAT) }}</div>
        <text-input
          ref="formatField"
          class="input-fields"
          :value="format"
          @on-text-input-change="onFormatChanged"
        />
      </div>
    </div>
    <div class="m-row" v-if="value">
      <div class="m-column_element">
        <div class="m-column_item"></div>
        <user-action
          :all-sources="allSources"
          :selected-source="selectedSource"
          :data-type="'string'"
          :action="action"
          :app-id="appId"
          @on-user-action-change="onUserActionChange"
        />
      </div>
    </div>
    <div class="m-row">
      <div class="m-column_element">
        <div class="m-column_item">
          {{ $t("wrapLines") }}
        </div>
        <b-form-checkbox
          v-model="lineWrap"
          @change="onWrapLinesChanged"
        ></b-form-checkbox>
      </div>
    </div>

    <div
      class="m-column_del"
      tabindex="0"
      @keydown.enter.stop="deleteColumn"
      @click="deleteColumn"
    >
      <svg>
        <use :xlink:href="getUtilityIcon('delete')"></use>
      </svg>
    </div>
  </section>
</template>

<script lang="js">
  import store from "@/store";
  import iconsUri from "@/views/drag-drop-pages/components/utils/icons-uri";
  import expressionBuilder from "@/views/drag-drop-pages/components/utils/expression-builder";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import TextInput from "@/views/drag-drop-pages/components/TextInput";
  import UserAction from "@/views/drag-drop-pages/components/builder/modal/UserAction";
  import Expressions from "@/components/Expressions";
  import dragAndDrop from "@/views/drag-drop-pages/components/utils/dragAndDrop";

  export default  {
    name: "column-element",
    components: {
      SearchBoxSimple,
      TextInput,
      UserAction,
      Expressions
    },
    props: {
      id: {
        type: String
      },
      columnData: {
        type: Object
      },
      allSources: {
        type: Object
      },
      selectedSource: {
        type: Object
      },
      appType: {
        type: String
      }
    },
    computed: {
      messages() {
        return store.state.messages;
      },
      appId() {
        return dragAndDrop.getAppId();
      }
    },
    mounted () {
      const valueOptions = [];
      if (Array.isArray(this.selectedSource.fields)) {
        this.selectedSource.fields.forEach(f => {
          f[this.propName] = f.as ? f.as : f.field;
          valueOptions.push(f);
        })
      } else {
        for (const [key, value] of Object.entries(this.selectedSource.fields)) {
          value[this.propName] = key;
          valueOptions.push(value);
        }
      }
      this.valueOptions = valueOptions;
      this.header = this.columnData.headerExpression;
      if(this.columnData.field) {
        this.value = this.valueOptions.find(el => el[this.propName] === this.columnData.field.name);
        this.format = this.columnData.field.format;
        this.action = this.columnData.field.action;
      }
      this.lineWrap = this.columnData.lineWrap;
      this.onUserSortableChanged(this.columnData.userSortable);
      this.isLocaleAwareFormatting = !this.format;
      this.updateIsDateFormat();
      this.isReady = true;
    },
    data () {
      return {
        isReady: false,
        isLocaleAwareFormatting: true,
        valueOptions: [],
        header: null,
        value: null,
        format: null,
        action: null,
        propName: "name",
        lineWrap: false,
        userSortable: false
      }
    },
    methods: {
      updateIsDateFormat() {
        this.isDateFormat = false;
        if(this.value) {
          this.isDateFormat = this.value.dataType.toLowerCase() === expressionBuilder.EXPRESSION_TYPE.DATE;
        }
      },
      toggleIsLocaleAware() {
        this.isLocaleAwareFormatting = !this.isLocaleAwareFormatting;
      },
      onCheckboxChanged(val) {
        this.isLocaleAwareFormatting = val;
        this.emitEvent();
      },
      getUtilityIcon(iconName) {
        return iconsUri.getUtilityUrl(iconName);
      },
      emitEvent() {
        const obj = {
          id: this.columnData.id,
          rowId: this.columnData.rowId,
          headerExpression: this.header,
          lineWrap: this.lineWrap || null,
          userSortable: this.userSortable || null
        };
        if(this.header) {
          obj.headerExpression = this.header;
        }
        if(this.value) {
          obj.field = {
            dataType: this.value.dataType,
            name: this.value[this.propName]
          };
          if(this.isDateFormat) {
            obj.isLocaleAwareFormatting = this.isLocaleAwareFormatting;
            obj.field.format = this.isLocaleAwareFormatting? null : this.format;
          }
          if(this.action) {
            obj.field.action = this.action;
          }
        }
        const EVENT = "on-selected-field-column";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          this.$emit(EVENT, obj);
        }
      },
      onFormatChanged(txt) {
        this.format = txt;
        this.emitEvent();
      },
      onValueSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.value = null;
          this.emitEvent();
        } else {
          this.value = ctx;
          this.updateIsDateFormat();
          this.emitEvent();
        }
      },
      onConfigExpressionValid(ctx) {
        this.header = ctx.status? ctx.confExpression : null;
        this.emitEvent();
      },
      onHeaderCleared() {
        this.header = null;
        this.emitEvent();
      },
      deleteColumn() {
        /**
         * Triggers delete event
         */
        const EVENT = "on-delete-column";
        if(this.$listeners[EVENT] || this._events[EVENT]) {
          this.$emit(EVENT, this.columnData);
        }
      },
      onUserActionChange(ctx) {
        this.action = ctx;
        this.emitEvent();
      },
      onWrapLinesChanged(val) {
        this.lineWrap = val;
        this.emitEvent();
      },
      onUserSortableChanged(val) {
        this.userSortable = val;
        this.emitEvent();
      }
    }
  }
</script>

<style scoped lang="scss">
  @import "@/styles/components/main";
  @include drag-drop-a-tag;
  .m-column {
    @include mixin-box;
    position: relative;
    padding: 20px 12px;
    margin-bottom: 4px;
    box-sizing: border-box;
  }
  .m-row {
    margin-bottom: 12px;
  }
  .m-column_element {
    display: flex;
    flex-direction: column;
    grid-template-columns: 100px auto 1.2rem;
    grid-template-rows: auto;
    margin-bottom: 10px;
  }
  .m-column_del {
    position: absolute;
    top: 0;
    right: 0;
    padding: 0.3rem;
    fill: #c23934;
    cursor: pointer;
    svg {
      height: 18px;
      width: 18px;
    }
  }
</style>
